import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import * as Icons from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { contactsApi } from '../api/endpoints';
import { useAuth } from '../context/AuthContext';
import { DeleteButton } from '../components/DeleteButton';
import { toast } from 'sonner';

// --- ADVANCED CSV IMPORT DIALOG COMPONENT ---
const ImportContactsDialog = ({ onImported }) => {
  const [open, setOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parsedData, setParsedData] = useState([]);
  const [errorSummary, setErrorSummary] = useState({ skipped: 0, total: 0 });
  const fileInputRef = useRef(null);

  const resetImportState = () => {
    setParsedData([]);
    setErrorSummary({ skipped: 0, total: 0 });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Invalid file type. Please upload a standard .csv spreadsheet.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      if (typeof text !== 'string') return;

      const lines = text.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
      if (lines.length < 2) {
        toast.error('The selected CSV file appears to have no data rows.');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/[^a-z0-9]/g, ''));
      
      const nameIdx = headers.findIndex(h => h.includes('name') || h === 'fullname');
      const phoneIdx = headers.findIndex(h => h.includes('phone') || h.includes('mobile') || h === 'tel');
      const emailIdx = headers.findIndex(h => h.includes('email') || h === 'mail');
      const companyIdx = headers.findIndex(h => h.includes('company') || h.includes('org') || h === 'firm');
      const cityIdx = headers.findIndex(h => h.includes('city') || h === 'location' || h === 'town');
      const tagsIdx = headers.findIndex(h => h.includes('tags') || h === 'label');

      if (nameIdx === -1 || phoneIdx === -1) {
        toast.error('Missing required columns. Your CSV headers must contain fields for "Name" and "Phone".');
        return;
      }

      const validContacts = [];
      let skippedCount = 0;

      for (let i = 1; i < lines.length; i++) {
        const row = lines[i].split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map(v => v.replace(/^"|"$/g, '').trim());
        
        if (row.length === 0 || (row.length === 1 && !row[0])) continue;

        const nameValue = row[nameIdx];
        const phoneValue = row[phoneIdx];

        if (!nameValue || !phoneValue) {
          skippedCount++;
          continue;
        }

        validContacts.push({
          name: nameValue,
          phone: phoneValue,
          email: emailIdx !== -1 ? row[emailIdx] || '' : '',
          company: companyIdx !== -1 ? row[companyIdx] || 'Self' : 'Self',
          city: cityIdx !== -1 ? row[cityIdx] || 'Chh. Sambhajinagar' : 'Chh. Sambhajinagar',
          tags: tagsIdx !== -1 && row[tagsIdx] ? row[tagsIdx].split(/[;|]/).map(t => t.trim()).filter(Boolean) : [],
        });
      }

      setParsedData(validContacts);
      setErrorSummary({ skipped: skippedCount, total: lines.length - 1 });
      
      if (validContacts.length === 0) {
        toast.error('No usable records found. Ensure full names and phone fields are populated.');
      } else {
        toast.success(`Successfully read ${validContacts.length} contacts from data payload.`);
      }
    };

    reader.readAsText(file);
  };

  const processImport = async () => {
    if (parsedData.length === 0) return;
    setImporting(true);

    try {
      if (typeof contactsApi.bulkCreate === 'function') {
        const savedList = await contactsApi.bulkCreate({ contacts: parsedData });
        onImported(savedList, false);
      } else {
        const batchPromises = parsedData.map(contact => contactsApi.create(contact).catch(() => null));
        const resolvedBatches = await Promise.all(batchPromises);
        const successfulCreations = resolvedBatches.filter(Boolean);
        onImported(successfulCreations, true);
      }

      toast.success(`Pipeline integration clear! Imported ${parsedData.length} records.`);
      setOpen(false);
      resetImportState();
    } catch (error) {
      toast.error('Backend structural failure encountered during bulk processing.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetImportState(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="hidden sm:inline-flex">
          <Icons.Upload className="w-4 h-4 mr-1.5" /> Import
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Import Directory CSV</DialogTitle>
          <DialogDescription>
            Seamlessly batch upload client lists. Columns matching <b>"Name"</b> and <b>"Phone"</b> are strictly mandatory.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div 
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-slate-200 hover:border-brand-navy rounded-lg p-6 text-center cursor-pointer bg-slate-50 hover:bg-slate-50/50 transition-colors"
          >
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileChange} 
              accept=".csv" 
              className="hidden" 
            />
            <Icons.FileSpreadsheet className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <span className="text-sm font-medium text-slate-600 block">
              Click to browse local CSV sheets
            </span>
            <span className="text-xs text-slate-400 block mt-1">Accepts comma-separated format values</span>
          </div>

          {errorSummary.total > 0 && (
            <div className="rounded-md p-3 space-y-1.5 border border-slate-100 bg-slate-50 text-xs text-slate-600">
              <div className="flex justify-between">
                <span>Total processed records:</span>
                <span className="font-bold text-slate-900">{errorSummary.total}</span>
              </div>
              <div className="flex justify-between text-emerald-700">
                <span>Validated for database entry:</span>
                <span className="font-bold">{parsedData.length}</span>
              </div>
              {errorSummary.skipped > 0 && (
                <div className="flex justify-between text-amber-700 border-t border-slate-200/60 pt-1.5 mt-1.5">
                  <span>Skipped due to missing required data fields:</span>
                  <span className="font-bold">{errorSummary.skipped}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={importing}>Cancel</Button>
          <Button 
            className="bg-brand-navy hover:bg-[#13294A]" 
            onClick={processImport} 
            disabled={parsedData.length === 0 || importing}
          >
            {importing ? 'Processing Sheet...' : 'Execute Batch Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ContactFormDialog = React.memo(({ onSaved, trigger, initial }) => {
  const isEdit = !!initial;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: 'Self', city: 'Chh. Sambhajinagar', tags: '',
  });

  useEffect(() => {
    if (open) {
      setForm({
        name: initial?.name || '',
        email: initial?.email || '',
        phone: initial?.phone || '',
        company: initial?.company || 'Self',
        city: initial?.city || 'Chh. Sambhajinagar',
        tags: initial?.tags?.join(', ') || '',
      });
    }
  }, [open, initial]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name?.trim() || !form.phone?.trim()) { 
      toast.error('Name and phone are required'); 
      return; 
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };
      const saved = isEdit
        ? await contactsApi.update(initial.id, payload)
        : await contactsApi.create(payload);
      onSaved(saved, isEdit);
      toast.success(isEdit ? 'Contact updated' : 'Contact added successfully');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save contact');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isEdit ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
          <DialogDescription>{isEdit ? 'Update this contact in your directory.' : 'Create a contact record in the Kiana directory.'}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label className="font-bold uppercase text-[11px] mb-1 block">Full Name *</Label><Input value={form.name} onChange={e => update('name', e.target.value)} /></div>
          <div><Label className="font-bold uppercase text-[11px] mb-1 block">Email</Label><Input type="email" value={form.email} onChange={e => update('email', e.target.value)} /></div>
          <div><Label className="font-bold uppercase text-[11px] mb-1 block">Phone *</Label><Input value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+91 9XXXXXXXXX" /></div>
          <div><Label className="font-bold uppercase text-[11px] mb-1 block">Company</Label><Input value={form.company} onChange={e => update('company', e.target.value)} /></div>
          <div><Label className="font-bold uppercase text-[11px] mb-1 block">City</Label><Input value={form.city} onChange={e => update('city', e.target.value)} /></div>
          <div className="sm:col-span-2"><Label className="font-bold uppercase text-[11px] mb-1 block">Tags (comma separated)</Label><Input value={form.tags} onChange={e => update('tags', e.target.value)} placeholder="VIP, Investor, Referral" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="bg-brand-navy hover:bg-[#13294A]" onClick={submit} disabled={saving}>{saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Add Contact')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
ContactFormDialog.displayName = 'ContactFormDialog';

const KNOWN_SOURCES = ['Meta (Facebook)', 'Instagram', 'Google Ads', 'WhatsApp Campaign', 'Other Ads', 'Website', 'Referral', 'Walk-in'];

const ContactStats = ({ data }) => {
  const total = data.length;
  const today = new Date().toISOString().slice(0, 10);
  const newToday = data.filter(c => (c.lastContact || '').startsWith(today)).length;

  // Source breakdown — derive from tags (we tag with source on creation)
  const sourceCounts = {};
  for (const c of data) {
    const tags = c.tags || [];
    let matched = null;
    for (const t of tags) {
      if (KNOWN_SOURCES.includes(t)) { matched = t; break; }
    }
    const src = matched || 'Other';
    sourceCounts[src] = (sourceCounts[src] || 0) + 1;
  }
  const breakdown = KNOWN_SOURCES.map(s => ({ name: s, count: sourceCounts[s] || 0 }));
  if (sourceCounts.Other) breakdown.push({ name: 'Other', count: sourceCounts.Other });

  const SOURCE_ICONS = {
    'Meta (Facebook)': 'Facebook', 'Instagram': 'Instagram', 'Google Ads': 'Search',
    'WhatsApp Campaign': 'MessageCircle', 'Other Ads': 'Megaphone', 'Website': 'Globe',
    'Referral': 'Share2', 'Walk-in': 'Footprints', 'Other': 'CircleDashed',
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="contact-stats">
      <div className="rounded-xl bg-gradient-to-r from-[#0F1F38] via-[#1B3A5C] to-[#2A4F7A] text-white p-4">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-gold mb-1">Total Contacts</div>
        <div className="font-display text-3xl font-bold">{total}</div>
        <div className="text-xs text-slate-300 mt-0.5">{newToday} added today</div>
      </div>
      <Card className="border-slate-200 md:col-span-2">
        <CardContent className="p-4">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Source-wise Segmentation</div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
            {breakdown.map(b => {
              const Icon = Icons[SOURCE_ICONS[b.name] || 'CircleDashed'];
              return (
                <div key={b.name} className="text-center" data-testid={`contact-src-${b.name.replace(/\s+/g, '-')}`}>
                  <div className="w-9 h-9 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-1">
                    <Icon className="w-4 h-4 text-brand-navy" />
                  </div>
                  <div className="font-display text-lg font-bold">{b.count}</div>
                  <div className="text-[10px] text-slate-500 leading-tight">{b.name}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Contacts = () => {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('contacts', 'create') ?? true;
  const canEdit = hasPermission('contacts', 'edit') ?? true;
  const canDelete = hasPermission('contacts', 'delete') ?? true;

  const [data, setData] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try { 
        const res = await contactsApi.list(); 
        if (isMounted) setData(res || []);
      } catch (e) { 
        toast.error('Failed to clear client directory payload');
      } finally { 
        if (isMounted) setLoading(false); 
      }
    })();
    return () => { isMounted = false; };
  }, []);

  const filtered = useMemo(() => {
    const searchStr = q.toLowerCase().trim();
    if (!searchStr) return data;
    return data.filter(c =>
      (c.name || '').toLowerCase().includes(searchStr) ||
      (c.email || '').toLowerCase().includes(searchStr) ||
      (c.phone || '').toLowerCase().includes(searchStr) ||
      (c.company || '').toLowerCase().includes(searchStr) ||
      (c.city || '').toLowerCase().includes(searchStr)
    );
  }, [data, q]);

  const onSaved = useCallback((saved, isEdit) => {
    if (isEdit) setData(d => d.map(x => x.id === saved.id ? saved : x));
    else setData(d => [saved, ...d]);
  }, []);

  const onBulkImported = useCallback((importedData, requiresFlattening) => {
    if (requiresFlattening) {
      setData(d => [...importedData, ...d]);
    } else {
      setData(d => Array.isArray(importedData) ? [...importedData, ...d] : [importedData, ...d]);
    }
  }, []);

  // --- REUSABLE CSV EXPORT CONTROLLER ---
  const handleExport = useCallback(() => {
    if (filtered.length === 0) {
      toast.error('There are no active records available to export.');
      return;
    }

    // Defining column row keys matching user layout data structure
    const columns = [
      { label: 'Full Name', key: 'name' },
      { label: 'Email Address', key: 'email' },
      { label: 'Phone Number', key: 'phone' },
      { label: 'Company Organization', key: 'company' },
      { label: 'City Location', key: 'city' },
      { label: 'Profile Tags', key: 'tags' },
      { label: 'Last Engagement Contact', key: 'lastContact' }
    ];

    // Building header line
    const headerRow = columns.map(col => `"${col.label.replace(/"/g, '""')}"`).join(',');

    // Processing matching content structures map safely sanitizing nested symbols/commas
    const dataRows = filtered.map(row => {
      return columns.map(col => {
        let rawVal = row[col.key];
        
        // Flatten string tag arrays to alternate standard multi-split semi-colons
        if (col.key === 'tags' && Array.isArray(rawVal)) {
          rawVal = rawVal.join('; ');
        }
        
        const textVal = rawVal ? String(rawVal) : '';
        // Sanitize inner quote definitions by escaping them to double quotes standard
        return `"${textVal.replace(/"/g, '""')}"`;
      }).join(',');
    });

    const csvContent = [headerRow, ...dataRows].join('\n');
    
    // Creating native micro browser download injection pipeline
    try {
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const downloadUrl = URL.createObjectURL(blob);
      const tempLink = document.createElement('a');
      
      const fileTimestamp = new Date().toISOString().split('T')[0];
      tempLink.href = downloadUrl;
      tempLink.setAttribute('download', `Kiana_Directory_Export_${fileTimestamp}.csv`);
      document.body.appendChild(tempLink);
      
      tempLink.click();
      
      // Cleanup garbage DOM memory references immediately
      document.body.removeChild(tempLink);
      URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Export clear! File download created for ${filtered.length} listings.`);
    } catch (err) {
      toast.error('System failed to execute native file write stream downloads.');
    }
  }, [filtered]);

  const onDelete = useCallback(async (c) => {
    try {
      await contactsApi.remove(c.id);
      setData(d => d.filter(x => x.id !== c.id));
      toast.success(`Removed ${c.name}`);
    } catch (e) {
      toast.error('Failed to remove contact configuration');
    }
  }, []);

  const getInitials = (name) => {
    if (!name) return '??';
    return name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-5">
      <ContactStats data={data} />

      <Card className="border-slate-200">
        <CardContent className="p-3 sm:p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search contacts..." value={q} onChange={e => setQ(e.target.value)} />
          </div>
          
          <ImportContactsDialog onImported={onBulkImported} />

          {/* Activated Export Element Execution Endpoint */}
          <Button variant="outline" size="sm" className="hidden sm:inline-flex" onClick={handleExport}>
            <Icons.Download className="w-4 h-4 mr-1.5" /> Export
          </Button>

          {canCreate && (
            <ContactFormDialog
              onSaved={onSaved}
              trigger={
                <Button className="bg-brand-navy hover:bg-[#13294A] text-white">
                  <Icons.UserPlus className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline font-bold">New Contact</span>
                </Button>
              }
            />
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {loading && Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-44 w-full" />)}
        
        {!loading && filtered.length === 0 && (
          <div className="col-span-full text-center py-12 bg-white rounded-lg border border-slate-200 text-slate-400 font-medium">
            No contacts found matching your search.
          </div>
        )}

        {!loading && filtered.map((c) => (
          <Card key={c.id} className="border-slate-200 card-lift">
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <Avatar className="w-12 h-12">
                  <AvatarFallback className="bg-brand-navy text-white text-sm font-bold">
                    {getInitials(c.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate">{c.company}</div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(c.tags || []).map((t) => (
                      <Badge key={t} variant="outline" className="text-[10px] border-slate-200 text-slate-600 font-medium">{t}</Badge>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  {canEdit && (
                    <ContactFormDialog
                      initial={c}
                      onSaved={onSaved}
                      trigger={<Button size="icon" variant="ghost" className="h-8 w-8" title="Edit"><Icons.Pencil className="w-3.5 h-3.5" /></Button>}
                    />
                  )}
                  {canDelete && (
                    <DeleteButton
                      title={`Remove ${c.name}?`}
                      description="The contact will be deleted permanently."
                      onConfirm={() => onDelete(c)}
                    />
                  )}
                </div>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600 break-all"><Icons.Mail className="w-4 h-4 text-slate-400 shrink-0" /> {c.email || '—'}</div>
                <div className="flex items-center gap-2 text-slate-600"><Icons.Phone className="w-4 h-4 text-slate-400 shrink-0" /> {c.phone}</div>
                <div className="flex items-center gap-2 text-slate-600"><Icons.MapPin className="w-4 h-4 text-slate-400 shrink-0" /> {c.city || '—'}</div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                <div className="text-[11px] text-slate-500">Last <span className="font-medium text-slate-700">{c.lastContact || 'Never'}</span></div>
                <div className="flex gap-1">
                  <a href={`tel:${c.phone}`} onClick={(e) => !c.phone && e.preventDefault()}>
                    <Button size="icon" variant="outline" className="h-8 w-8" disabled={!c.phone} title="Call Contact"><Icons.Phone className="w-3.5 h-3.5" /></Button>
                  </a>
                  <a href={`mailto:${c.email}`} onClick={(e) => !c.email && e.preventDefault()}>
                    <Button size="icon" variant="outline" className="h-8 w-8" disabled={!c.email} title="Email Contact"><Icons.Mail className="w-3.5 h-3.5" /></Button>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Contacts;